import React, { useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const sections = [
    {
      id: 'challenge',
      title: 'Challenge',
      icon: 'AlertTriangle',
      content: project.challenge
    },
    {
      id: 'partnership',
      title: 'Partnership',
      icon: 'Handshake',
      content: project.partnership
    },
    {
      id: 'innovation',
      title: 'Innovation',
      icon: 'Lightbulb',
      content: project.innovation
    },
    {
      id: 'impact',
      title: 'Impact',
      icon: 'TrendingUp',
      content: project.impact
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative glass-morph bg-card/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="relative">
          <div className="aspect-video overflow-hidden">
            <Image
              src={project.heroImage}
              alt={project.title}
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent">
            <div className="absolute bottom-6 left-6 right-6">
              <div className="flex items-center space-x-3 mb-4">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-white">
                  {project.industry}
                </span>
                <span className="px-3 py-1 bg-accent/80 rounded-full text-sm font-medium text-white">
                  {project.projectType}
                </span>
              </div>
              
              <h2 className="text-3xl font-bold text-white font-heading mb-2">
                {project.title}
              </h2>
              
              <p className="text-white/90 text-lg">
                {project.description}
              </p>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-200"
          >
            <Icon name="X" size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto">
          <div className="p-6 space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {project.keyMetrics.map((metric, index) => (
                <div key={index} className="text-center p-4 glass-morph bg-primary/20 backdrop-blur-sm border border-white/20 rounded-lg">
                  <div className="text-2xl font-bold text-brand-cyan">
                    {metric.value}
                  </div>
                  <div className="text-sm text-white/70">
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Story Sections */}
            <div className="space-y-6">
              {sections.map((section, index) => (
                <div key={section.id} className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={section.icon} size={20} className="text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-text-primary font-heading">
                      {section.title}
                    </h3>
                  </div>
                  
                  <div className="pl-13 text-text-secondary leading-relaxed">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>

            {/* Technologies & Team */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-semibold text-white flex items-center space-x-2">
                  <Icon name="Cpu" size={18} className="text-brand-cyan" />
                  <span>Technologies Used</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-secondary/40 text-white/80 border border-white/20 rounded-full text-sm"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold text-text-primary flex items-center space-x-2">
                  <Icon name="Users" size={18} className="text-primary" />
                  <span>Project Team</span>
                </h4>
                <div className="space-y-2">
                  {project.teamMembers.map((member, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-primary">
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-text-primary">
                          {member.name}
                        </div>
                        <div className="text-xs text-text-secondary">
                          {member.role}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Client Testimonial */}
            {project.testimonial && (
              <div className="bg-muted rounded-xl p-6">
                <div className="flex items-start space-x-4">
                  <Icon name="Quote" size={24} className="text-primary flex-shrink-0 mt-1" />
                  <div className="space-y-3">
                    <p className="text-text-primary italic leading-relaxed">
                      "{project.testimonial.quote}"
                    </p>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={18} className="text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-text-primary">
                          {project.testimonial.author}
                        </div>
                        <div className="text-sm text-text-secondary">
                          {project.testimonial.role}, {project.client}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-white/20 p-6">
          <div className="flex flex-wrap gap-4 justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                iconName="Share2"
                iconPosition="left"
              >
                Share Case Study
              </Button>
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
              >
                Download PDF
              </Button>
            </div>
            
            <Button
              variant="default"
              className="bg-brand-orange hover:bg-brand-orange/90"
              iconName="MessageCircle"
              iconPosition="left"
            >
              Discuss Similar Project
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;